import { Check, Loader2, Search } from "lucide-react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../store";
import { updateQuoteStatus } from "../store/slices/epcSlice";
import toast from "react-hot-toast";

export enum QuoteStatusLabels {
  UPLOAD_FILE = "Upload File",
  FILE_UPLOADED = "File Uploaded",
  LOAN_JANSAMARTH = "Upload File / Apply for Jansamarth loan",
  LOAN_APPLIED = "Loan Applied",
  LOAN_REJECTED = "Loan Rejected",
  LOAN_APPROVED = "Loan Approved",
  LOAN_DISBURSED = "Loan disbursed",
  SR_GENERATED = "SR Generated",
  SR_PAYMENT_DONE = "SR Payment Done",
  PREPARE_METERFILE = "Prepare Meterfile",
  METERFILE_PREPARED = "Meterfile Prepared",
  INTIMATION_INSTALLATION = "Intimation / Installation",
  COMMISSION_CERTIFICATE = "Commissioning Certificate Generated",
  CLAIM_SUBSIDY = "Claim Subsidy",
  SUBSIDY_REQUEST_DONE = "Subsidy Request Done",
  SUBSIDY_DISBURSED = "Subsidy Disbursed",
}

export enum QuoteStatusLabelsResticted {
  LOAN_APPLIED = "Loan Applied",
  LOAN_JANSAMARTH = "Upload File / Apply for Jansamarth loan",
  LOAN_REJECTED = "Loan Rejected",
  LOAN_APPROVED = "Loan Approved",
  LOAN_DISBURSED = "Loan disbursed",
  PREPARE_METERFILE = "Prepare Meterfile",
}
export enum QuoteStatusLabelsAccounts {
  SR_PAYMENT_DONE = "SR Payment Done",
}
const statusColors: Record<string, string> = {
  UPLOAD_FILE: "bg-slate-100 text-red-700",
  FILE_UPLOADED: "bg-slate-100 text-slate-700",
  LOAN_APPLIED: "bg-blue-100 text-blue-700",
  LOAN_JANSAMARTH: "bg-cyan-100 text-cyan-700",
  LOAN_REJECTED: "bg-red-100 text-red-700",
  LOAN_APPROVED: "bg-green-100 text-green-700",
  LOAN_DISBURSED: "bg-green-200 text-green-800",
  SR_GENERATED: "bg-purple-100 text-purple-700",
  SR_PAYMENT_DONE: "bg-emerald-100 text-emerald-700",
  PREPARE_METERFILE: "bg-amber-100 text-amber-700",
  METERFILE_PREPARED: "bg-orange-100 text-orange-700",
  INTIMATION_INSTALLATION: "bg-indigo-100 text-indigo-700",
  COMMISSION_CERTIFICATE: "bg-violet-100 text-violet-700",
  SUBSIDY_REQUEST_DONE: "bg-yellow-100 text-yellow-700",
  SUBSIDY_DISBURSED: "bg-green-100 text-green-800",
  CLAIM_SUBSIDY: "bg-teal-100 text-teal-700",
};
interface QuoteStatusBadgeProps {
  status?: string;
  quoteId: string;
  onStatusUpdated: (success: boolean) => void;
}
const QuoteStatusBadge = ({
  status,
  quoteId,
  onStatusUpdated,
}: QuoteStatusBadgeProps) => {
  const dispatch: RootDispatch = useDispatch();

  const role = useSelector((state: RootState) => state.auth.role);
  const isAccounts = role === "account";
  const isRestrictedRole = !(
    role === "admin" ||
    role === "bdm" ||
    role === "LE"
  );
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [remark, setRemark] = useState("");

  const [loanBank, setLoanBank] = useState("");
  const [loanAppliedDate, setLoanAppliedDate] = useState<Date | null>(null);
  const [loanApprovedDate, setLoanApprovedDate] = useState<Date | null>(null);
  const [loanRejectedDate, setLoanRejectedDate] = useState<Date | null>(null);
  const [loanBankJansamarth, setLoanBankJansamarth] = useState("");
  const showCustomBankInput = loanBank === "jansamarth";

  const [loading, setLoading] = useState(false);
  const statuses = Object.entries(
    isAccounts
      ? QuoteStatusLabelsAccounts
      : isRestrictedRole
        ? QuoteStatusLabelsResticted
        : QuoteStatusLabels,
  ).map(([key, label]) => ({
    key,
    label,
  }));

  const filteredStatuses = statuses.filter((item) =>
    item.label.toLowerCase().includes(search.toLowerCase()),
  );
  const submitStatusUpdate = async () => {
    if (!selectedStatus) return;

    // ✅ LOAN_APPLIED validation
    const config = loanStatusConfig[selectedStatus];

    if (config?.requireBank && !loanBank) {
      toast.error("Please select bank");
      return;
    }

    if (config?.requireDate) {
      const dateMissing =
        (selectedStatus === "LOAN_APPLIED" && !loanAppliedDate) ||
        (selectedStatus === "LOAN_APPROVED" && !loanApprovedDate) ||
        (selectedStatus === "LOAN_REJECTED" && !loanRejectedDate);

      if (dateMissing) {
        toast.error("Please select date");
        return;
      }
    }
    try {
      setLoading(true);

      const payload: any = {
        quoteId,
        status: selectedStatus,
        remark,
      };

      if (selectedStatus === "LOAN_APPLIED") {
        let bank = loanBank;
        if (loanBank === "jansamarth") {
          if (loanBankJansamarth.trim() === "") {
            toast.error("Please enter bank name");
            return;
          }
          bank = loanBankJansamarth;
        }
        payload.loanDetails = {
          isJansmarath: loanBank === "jansamarth",
          bankName: bank,
          dateApplied: loanAppliedDate,
        };
      }
      if (selectedStatus === "LOAN_APPROVED") {
        payload.loanDetails = {
          approvedDate: loanApprovedDate,
        };
      }

      if (selectedStatus === "LOAN_REJECTED") {
        payload.loanDetails = {
          rejectedDate: loanRejectedDate,
        };
      }
      const res = await dispatch(updateQuoteStatus(payload)).unwrap();

      onStatusUpdated?.(res.success ?? false);

      // reset
      setSelectedStatus(null);
      setRemark("");
      setLoanBank("");
      setLoanBankJansamarth("");
      setLoanAppliedDate(null);
      setLoanApprovedDate(null);
      setLoanRejectedDate(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loanStatusConfig: Record<
    string,
    { requireBank?: boolean; requireDate?: boolean }
  > = {
    LOAN_APPLIED: {
      requireBank: true,
      requireDate: true,
    },
    LOAN_APPROVED: {
      requireDate: true,
    },
    LOAN_REJECTED: {
      requireDate: true,
    },
  };

  return (
    <>
      {!status || status === "-" ? (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(true);
          }}
          className="rounded-md bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-500"
        >
          N/A
        </button>
      ) : (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(true);
          }}
          className={`rounded-md px-2 py-1 text-xs font-semibold transition-all hover:opacity-80 ${
            statusColors[status || ""] || "bg-neutral-100 text-neutral-700"
          }`}
        >
          {QuoteStatusLabels[status as keyof typeof QuoteStatusLabels] ||
            status}
        </button>
      )}

      {isOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div className="border-b p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Update Quote Status</h2>

                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={() => setIsOpen(false)}
                  className="rounded-lg p-2 hover:bg-neutral-100"
                >
                  ✕
                </button>
              </div>

              <div className="relative mt-4">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                />

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search status..."
                  className="w-full rounded-xl border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="max-h-[400px] overflow-y-auto p-2">
              {filteredStatuses.map((item) => (
                <button
                  key={item.key}
                  disabled={loading}
                  onClick={() => {
                    setSelectedStatus(item.key);
                    setIsOpen(false);
                  }}
                  className={`flex w-full items-center justify-between rounded-xl p-3 text-left transition-all hover:bg-neutral-50 ${
                    status === item.key
                      ? "border border-green-200 bg-green-50"
                      : ""
                  }`}
                >
                  <div>
                    {/* <div className="font-medium">{item.label}</div> */}

                    <div
                      className={`mt-1 inline-flex rounded-md px-2 py-1 text-xs font-bold ${
                        statusColors[item.key]
                      }`}
                    >
                      {item.label}
                    </div>
                  </div>

                  {status === item.key ? (
                    <Check size={18} className="text-green-600" />
                  ) : null}
                </button>
              ))}

              {!filteredStatuses.length && (
                <div className="p-8 text-center text-sm text-neutral-500">
                  No status found
                </div>
              )}
            </div>

            <div className="border-t p-4">
              <button
                onClick={() => setIsOpen(false)}
                disabled={loading}
                className="w-full rounded-xl border py-2 text-sm font-medium hover:bg-neutral-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={16} className="animate-spin" />
                    Updating...
                  </span>
                ) : (
                  "Close"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {selectedStatus && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40"
          onClick={() => setSelectedStatus(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            <h2 className="text-lg font-semibold mb-3">
              Update Status →{" "}
              {
                QuoteStatusLabels[
                  selectedStatus as keyof typeof QuoteStatusLabels
                ]
              }
            </h2>

            {/* Remark (always) */}
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Add remark..."
              className="w-full rounded-xl border p-2 text-sm mb-3"
            />

            {/* LOAN_APPLIED extra fields */}
            <div className="space-y-3 mb-3">
              {selectedStatus === "LOAN_APPLIED" && (
                <>
                  <select
                    value={loanBank}
                    onChange={(e) => setLoanBank(e.target.value)}
                    className="w-full rounded-xl border p-2 text-sm"
                  >
                    <option value="">Select Bank</option>
                    <option value="Ecofy">Ecofy</option>
                    <option value="Solfin">Solfin</option>
                    <option value="Bajaj">Bajaj</option>
                    <option value="Ratnafin">Ratnafin</option>
                    {!isRestrictedRole && (
                      <option value="jansamarth">Jansamarth</option>
                    )}
                  </select>
                  {showCustomBankInput && (
                    <div>
                      {" "}
                      <input
                        type="text"
                        value={loanBankJansamarth}
                        onChange={(e) => setLoanBankJansamarth(e.target.value)}
                        placeholder="Enter Bank Name"
                        className="w-full rounded-xl border p-2 text-sm"
                      />
                    </div>
                  )}
                  <div>
                    {renderDateInput(loanAppliedDate, setLoanAppliedDate)}
                  </div>
                </>
              )}

              {selectedStatus === "LOAN_APPROVED" &&
                renderDateInput(loanApprovedDate, setLoanApprovedDate)}

              {selectedStatus === "LOAN_REJECTED" &&
                renderDateInput(loanRejectedDate, setLoanRejectedDate)}
            </div>
            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedStatus(null)}
                className="w-1/2 border rounded-xl py-2"
              >
                Cancel
              </button>

              <button
                disabled={loading}
                onClick={() => submitStatusUpdate()}
                className="w-1/2 bg-blue-600 text-white rounded-xl py-2"
              >
                {loading ? "Updating..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuoteStatusBadge;
const renderDateInput = (value: Date | null, setValue: any) => (
  <input
    type="date"
    value={value ? value.toISOString().split("T")[0] : ""}
    onChange={(e) => setValue(e.target.value ? new Date(e.target.value) : null)}
    className="w-full rounded-xl border p-2 text-sm"
  />
);
